import type { QueryClient } from '@tanstack/react-query';

export const CONVERSATIONS_QUERY_KEY = ['conversations'] as const;
export const CONVERSATIONS_UNREAD_COUNT_QUERY_KEY = ['conversations', 'unread-count'] as const;
export const FOUND_OBJECT_STATS_QUERY_KEY = ['found-object-stats'] as const;

type ConversationsListCache = {
  conversations?: Array<{ id: number; unread_count?: number | null; [key: string]: unknown }>;
  total_unread?: number;
  [key: string]: unknown;
};

/**
 * L’ouverture d’une conversation marque les messages lus côté API, mais la liste
 * ne se rafraîchit qu’au prochain poll. On met à jour le cache tout de suite.
 */
export function optimisticallyMarkConversationRead(
  queryClient: QueryClient,
  conversationId: number
): void {
  if (!Number.isFinite(conversationId) || conversationId <= 0) {
    return;
  }

  let removedUnread = 0;

  queryClient.setQueriesData({ queryKey: CONVERSATIONS_QUERY_KEY }, (old: unknown) => {
    if (!old || typeof old !== 'object') {
      return old;
    }
    const cache = old as ConversationsListCache;
    if (!Array.isArray(cache.conversations)) {
      return old;
    }

    let delta = 0;
    let changed = false;
    const conversations = cache.conversations.map((c) => {
      if (c.id !== conversationId) {
        return c;
      }
      const current = c.unread_count ?? 0;
      if (current <= 0) {
        return c;
      }
      delta = current;
      changed = true;
      return { ...c, unread_count: 0 };
    });

    if (!changed) {
      return old;
    }

    removedUnread = Math.max(removedUnread, delta);

    return {
      ...cache,
      conversations,
      total_unread:
        typeof cache.total_unread === 'number'
          ? Math.max(0, cache.total_unread - delta)
          : cache.total_unread,
    };
  });

  if (removedUnread <= 0) {
    return;
  }

  queryClient.setQueryData(
    CONVERSATIONS_UNREAD_COUNT_QUERY_KEY,
    (old: { count?: number } | undefined) => {
      if (!old || typeof old.count !== 'number') {
        return old;
      }
      return { ...old, count: Math.max(0, old.count - removedUnread) };
    }
  );
}

export function invalidateMessagingCaches(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: CONVERSATIONS_UNREAD_COUNT_QUERY_KEY }),
  ]).then(() => undefined);
}

export function invalidateFoundObjectStats(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: FOUND_OBJECT_STATS_QUERY_KEY });
}
